import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next"; // Import translation hook
import Lottie from "lottie-react";
import animationData from "./Animation - 1724341893745.json";
import "./demoFlow.css";
import { CircleIcon, CheckCheckIcon } from "lucide-react";
import Swal from "sweetalert2";

function Progress() {
  const { t } = useTranslation("global"); // Initialize translation hook
  const navigate = useNavigate();
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [showAnimate, setShowAnimate] = useState(false);
  const [activateSubmit, setActivateSubmit] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [finalSubmit, setFinalSubmit] = useState(false);
  const [respondedQuestions, setRespondedQuestions] = useState(0);
  const [textareaValue, setTextareaValue] = useState("");
  const [isOpen, setIsOpen] = useState(true);
  const [activeDemo, setActiveDemo] = useState("");
  const [commentRequired, setCommentRequired] = useState(false);
  
  // Question states
  const [question1Done, setQuestion1Done] = useState(false);
  const [question2Done, setQuestion2Done] = useState(false);
  const [question3Done, setQuestion3Done] = useState(false);
  const [question4Done, setQuestion4Done] = useState(false);
  const [question5Done, setQuestion5Done] = useState(false);
  
  // Button validation states
  const [validButton1, setValidButton1] = useState(false);
  const [validButton2, setValidButton2] = useState(false);
  const [validButton3, setValidButton3] = useState(false);
  const [validButton4, setValidButton4] = useState(false);
  const [validButton5, setValidButton5] = useState(false);
  
  // Question active states
  const [activeQuestion, setActiveQuestion] = useState(1);
  const [filesUploaded, setFilesUploaded] = useState(false);
  const [file4Uploaded, setFile4Uploaded] = useState(false);

  const [question2Step, setQuestion2Step] = useState(1);
  const [question3Step, setQuestion3Step] = useState(1);
  const [question4Step, setQuestion4Step] = useState(1);
  const [question5Step, setQuestion5Step] = useState(1);
  
  // Refs for scrolling
  const submit = useRef(null);
  const question1Ref = useRef(null);
  const question2Ref = useRef(null);
  const question3Ref = useRef(null);
  const question4Ref = useRef(null);
  const question5Ref = useRef(null);
  
  // Initial loading
  useEffect(() => {
    // Simulate loading state for 1.5 seconds
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1500);
    
    // Start animation after 3 seconds
    const timer1 = setTimeout(() => {
      scrollToQuestion1();
      setShowAnimate(true);
    }, 3000);
    
    return () => {
      clearTimeout(timer);
      clearTimeout(timer1);
    };
  }, []);
  
  // Start demo animation after 5 seconds
  useEffect(() => {
    setTimeout(() => {
      setActiveDemo("start");
    }, 5000);
  }, []);
  
  // Scrolling functions
  const scrollToSubmitButton = () => {
    if (submit.current) {
      submit.current.scrollIntoView({ behavior: "smooth" });
    }
  };
  
  const scrollToQuestion = (ref, questionNumber) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }
    setRespondedQuestions(questionNumber);
    setActiveQuestion(questionNumber);
  };
  
  const scrollToQuestion1 = () => scrollToQuestion(question1Ref, 1);
  const scrollToQuestion2 = () => scrollToQuestion(question2Ref, 2);
  const scrollToQuestion3 = () => scrollToQuestion(question3Ref, 3);
  const scrollToQuestion4 = () => scrollToQuestion(question4Ref, 4);
  const scrollToQuestion5 = () => scrollToQuestion(question5Ref, 5);
  
  // Toggle accordion
  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };
  
  const percentage = ((respondedQuestions / 5) * 100).toFixed(2);
  
  // File handling functions
  const handleFileChange = (event) => {
    const files = event.target.files;
    // List of allowed file types
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
      "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
    ];
    
    if (files.length > 0) {
      const fileType = files[0].type;
      if (!allowedTypes.includes(fileType)) {
        Swal.fire({
          icon: "error",
          title: t("progress.file.unsupportedType"),
          text: t("progress.file.selectSupported"),
        });
        return;
      }
      
      setFilesUploaded(true);
      setValidButton2(true);
    }
  };
  
  const handleFileChangeQuestion4 = (event) => {
    const files = event.target.files;
    // List of allowed file types
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // docx
      "application/vnd.openxmlformats-officedocument.presentationml.presentation", // pptx
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // xlsx
    ];
    
    if (files.length > 0) {
      const fileType = files[0].type;
      if (!allowedTypes.includes(fileType)) {
        Swal.fire({
          icon: "error",
          title: t("progress.file.unsupportedType"),
          text: t("progress.file.selectSupported"),
        });
        return;
      }
      
      setFile4Uploaded(true);
      setQuestion4Step(4);
      setFinalSubmit(true);
    }
  };
  
  // Handle textarea input for validation
  const handleTextareaChangeQ3 = (e) => {
    const value = e.target.value;
    setTextareaValue(value);
    
    if (value.trim().length > 0) {
      setValidButton3(true);
      setQuestion3Step(3)
    } else {
      setValidButton3(false);
    }
  };

  const handleTextareaChangeQ5 = (e) => {
    const value = e.target.value;
    setTextareaValue(value);
    
    if (value.trim().length > 0) {
      setValidButton4(true);
      setQuestion5Step(4)
    } else {
      setValidButton5(false);
    }
  };
  
  // Button handlers
  const handleQuestion1Submit = () => {
    setQuestion1Done(true);
    setValidButton1(false);
    scrollToQuestion2();
  };
  
  const handleQuestion2Submit = () => {
    setQuestion2Done(true);
    setValidButton2(false);
    scrollToQuestion3();
  };
  
  const handleQuestion3Submit = () => {
    setQuestion3Done(true);
    setValidButton3(false);
    scrollToQuestion4();
  };
  
  const handleQuestion4Submit = () => {
    if (!file4Uploaded) {
      Swal.fire({
        title: t("progress.alerts.missingFile"),
        text: t("progress.alerts.addFile"),
        icon: "info",
        allowOutsideClick: false,
        confirmButtonText: t("progress.buttons.ok"),
      });
      setQuestion4Step(3);
    } else {
      setQuestion4Done(true);
      setValidButton4(false);
      scrollToQuestion5();
    }
  };
  
  const handleQuestion5Submit = () => {
    if (!commentRequired) {
      Swal.fire({
        title: t("progress.alerts.requiredComment"),
        text: t("progress.alerts.addComment"),
        icon: "info",
        allowOutsideClick: false,
        confirmButtonText: t("progress.buttons.ok"),
      }).then(() => {
        setCommentRequired(true);
        setQuestion5Step(3);
      });
    } else {
      setQuestion5Done(true);
      setValidButton5(false);
      scrollToSubmitButton();
      setActivateSubmit(true);
      setIsOpen(false);
    }
  };
  
  const handleFinalSubmit = () => {
    setActivateSubmit(false);
    setShowAnimate(false);
    setButtonLoading(true);
    setTimeout(() => {
      setButtonLoading(false);
      Swal.fire({
        title: t("progress.alerts.congratulations"),
        text: t("progress.alerts.submitSuccess"),
        icon: "success",
      }).then(() => {
        setTimeout(() => {
        // navigate("/");
        window.location.href = "/";
        }, 500);
      });
    }, 1000);
  };

  useEffect(() => {
    if (question1Done) {
      scrollToQuestion2();
    } 
  }, [question1Done]);
  
  useEffect(() => {
    if (question2Done) {
      scrollToQuestion3();
    }
  }, [question2Done]);
  
  useEffect(() => {
    if (question3Done) {
      scrollToQuestion4();
    }
  }, [question3Done]);
  
  useEffect(() => {
    if (question4Done) {
      scrollToQuestion5();
    }
  }, [question4Done]);

  // Loading screen
  if (loading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-t-blue-600 border-blue-200 rounded-full animate-spin mx-auto"></div>
          <h2 className="mt-6 text-2xl font-bold text-blue-600">{t("progress.loading.title")}</h2>
          <p className="mt-2 text-gray-600">{t("progress.loading.description")}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{t("progress.header.title")}</h1>
              <p className="text-gray-600">{t("progress.header.section")}: {t("demo.section2")}</p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-32 bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-blue-600 h-2.5 rounded-full transition-all duration-500" 
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <span className="text-sm font-medium text-gray-600">{percentage}%</span>
            </div>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left sidebar */}
          <div className="w-full md:w-1/4 lg:w-1/5">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden sticky top-6">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-800">{t("home.1")}</h2>
                    <p className="text-sm text-gray-500">{t("home.ssi-full")}</p>
                  </div>
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {t("demo.level2")}
                  </span>
                </div>
              </div>
              
              <div className="divide-y divide-gray-200">
                <div className="p-3 bg-gray-50 text-gray-500">
                  <button className="flex items-center justify-between w-full text-left" disabled>
                    <span className="font-medium">{t("demo.section1")}</span>
                    <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">5/9</span>
                  </button>
                </div>
                
                <div className="p-3 bg-white">
                  <button 
                    className="flex items-center justify-between w-full text-left font-medium text-blue-600" 
                    onClick={toggleAccordion}
                  >
                    <span>{t("demo.section2")}</span>
                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                      {respondedQuestions}/5
                    </span>
                    {isOpen ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    )}
                  </button>
                  
                  {isOpen && (
                    <ul className="mt-2 pl-2 space-y-1 text-sm">
                      <li 
                        onClick={scrollToQuestion1}
                        className="flex items-center py-1 px-2 rounded-md hover:bg-gray-50 cursor-pointer"
                      >
                        {question1Done ? (
                          <CheckCheckIcon className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <CircleIcon className="h-3 w-3 mr-2 text-gray-400" />
                        )}
                        <span className={question1Done ? "text-green-700" : "text-gray-700"}>
                          {t("progress.questions.q1")}
                        </span>
                      </li>
                      
                      <li 
                        onClick={scrollToQuestion2}
                        className="flex items-center py-1 px-2 rounded-md hover:bg-gray-50 cursor-pointer"
                      >
                        {question2Done ? (
                          <CheckCheckIcon className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <CircleIcon className="h-3 w-3 mr-2 text-gray-400" />
                        )}
                        <span className={question2Done ? "text-green-700" : "text-gray-700"}>
                          {t("progress.questions.q2")}
                        </span>
                      </li>
                      
                      <li 
                        onClick={scrollToQuestion3}
                        className="flex items-center py-1 px-2 rounded-md hover:bg-gray-50 cursor-pointer"
                      >
                        {question3Done ? (
                          <CheckCheckIcon className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <CircleIcon className="h-3 w-3 mr-2 text-gray-400" />
                        )}
                        <span className={question3Done ? "text-green-700" : "text-gray-700"}>
                          {t("demo.question3")}
                        </span>
                      </li>
                      
                      <li 
                        onClick={scrollToQuestion4}
                        className="flex items-center py-1 px-2 rounded-md hover:bg-gray-50 cursor-pointer"
                      >
                        {question4Done ? (
                          <CheckCheckIcon className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <CircleIcon className="h-3 w-3 mr-2 text-gray-400" />
                        )}
                        <span className={question4Done ? "text-green-700" : "text-gray-700"}>
                          {t("demo.question4")}
                        </span>
                      </li>
                      
                      <li 
                        onClick={scrollToQuestion5}
                        className="flex items-center py-1 px-2 rounded-md hover:bg-gray-50 cursor-pointer"
                      >
                        {question5Done ? (
                          <CheckCheckIcon className="h-4 w-4 mr-2 text-green-500" />
                        ) : (
                          <CircleIcon className="h-3 w-3 mr-2 text-gray-400" />
                        )}
                        <span className={question5Done ? "text-green-700" : "text-gray-700"}>
                          {t("demo.question5")}
                        </span>
                      </li>
                    </ul>
                  )}
                </div>
                
                <div className="p-3 bg-gray-50 text-gray-500">
                  <button className="flex items-center justify-between w-full text-left" disabled>
                    <span className="font-medium">{t("demo.section3")}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full">0/16</span>
                  </button>
                </div>
                
                <div className="p-3 bg-gray-50 text-gray-500">
                  <button className="flex items-center justify-between w-full text-left" disabled>
                    <span className="font-medium">{t("demo.section4")}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full">0/12</span>
                  </button>
                </div>
                
                <div className="p-3 bg-gray-50 text-gray-500">
                  <button className="flex items-center justify-between w-full text-left" disabled>
                    <span className="font-medium">{t("demo.section5")}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full">0/4</span>
                  </button>
                </div>
                
                <div className="p-3 bg-gray-50 text-gray-500">
                  <button className="flex items-center justify-between w-full text-left" disabled>
                    <span className="font-medium">{t("demo.section6")}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full">0/7</span>
                  </button>
                </div>
                
                <div className="p-4">
                  <button
                    id="soumettre"
                    ref={submit}
                    disabled={buttonLoading || !activateSubmit}
                    type="button"
                    onClick={handleFinalSubmit}
                    className="w-full py-3 px-4 inline-flex justify-center items-center gap-2 rounded-md border border-transparent font-semibold bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all text-sm relative disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {buttonLoading ? (
                      <>
                        <div className="animate-spin inline-block w-4 h-4 border-[3px] border-current border-t-transparent text-white rounded-full"></div>
                        {t("progress.buttons.processing")}
                      </>
                    ) : (
                      <>
                        {t("progress.buttons.submitAnswers")}
                        {activateSubmit && (
                          <div className="absolute right-2">
                            <Lottie
                              animationData={animationData}
                              loop={true}
                              style={{ width: 60, height: 60 }}
                            />
                          </div>
                        )}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main content */}
          <div className="w-full md:w-3/4 lg:w-4/5 space-y-6">
            {/* Question 1 */}
            <div 
              ref={question1Ref}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">{t("progress.questions.question")} 1 {t("progress.questions.outOf")} 5</h2>
                </div>
                
                <div className="space-y-6">
                  <p className="text-gray-700 font-medium">
                    {t("demo.question1")}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <label className="flex p-4 cursor-pointer bg-white border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
                      <span className="text-gray-300 font-semibold">{t("progress.answers.yes")}</span>
                      <input 
                        type="radio" 
                        name="question1" 
                        disabled 
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                    
                    <label 
                      className={`flex p-4 cursor-pointer bg-white border ${
                        activeQuestion === 1 && !validButton1 && showAnimate
                          ? "border-blue-500 shadow-md animate-pulse"
                          : "border-gray-200"
                      } rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all relative`}
                    >
                      {activeQuestion === 1 && !validButton1 && showAnimate && (
                        <div className="absolute -right-2 -top-2">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 60, height: 60 }}
                          />
                        </div>
                      )}
                      <span className="text-gray-800 font-semibold">{t("progress.answers.no")}</span>
                      <input 
                        type="radio" 
                        name="question1" 
                        onChange={() => setValidButton1(true)}
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>

                    <label className="flex p-4 cursor-pointer bg-white border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
                      <span className="text-gray-300 font-semibold">{t("progress.answers.partially")}</span>
                      <input 
                        type="radio" 
                        name="question1"
                        disabled
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                  
                  <div>
                    <h3 className="block mb-2 text-sm font-medium text-gray-500">
                      {t("progress.file.supportingDocs")} (<span className="text-red-500 font-semibold">{t("progress.file.supportedTypes")}</span>)
                    </h3>
                    <div className="mt-1">
                      <div className="bg-gray-50 border border-gray-300 rounded-md px-3 py-2 opacity-50">
                        <input
                          type="file"
                          disabled
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="block mb-2 text-sm font-medium text-gray-700">{t("progress.comments.label")}</h3>
                    <textarea
                      disabled
                      rows={3}
                      className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm placeholder-gray-400 opacity-50 focus:border-blue-500 focus:ring-blue-500"
                      placeholder={t("progress.comments.placeholder")}
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={question1Done || !validButton1}
                      onClick={handleQuestion1Submit}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 relative disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {t("progress.buttons.validate")}
                      {validButton1 && (
                        <div className="absolute -right-1 -top-1">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 70, height: 70 }}
                          />
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Question 2 */}
            <div 
              ref={question2Ref}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">{t("progress.questions.question")} 2 {t("progress.questions.outOf")} 5</h2>
                </div>
                
                <div className="space-y-6">
                  <p className="text-gray-700 font-medium">
                    {t("demo.question2")}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <label 
                      className={`flex p-4 cursor-pointer bg-white border ${
                        activeQuestion === 2 && question2Step === 1 && !filesUploaded 
                          ? "border-blue-500 shadow-md animate-pulse" 
                          : "border-gray-200"
                      } rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all relative`}
                    >
                      {activeQuestion === 2 && question2Step === 1 && !filesUploaded && (
                        <div className="absolute -right-2 -top-2">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 60, height: 60 }}
                          />
                        </div>
                      )}
                      <span className="text-gray-800 font-semibold">{t("progress.answers.yes")}</span>
                      <input 
                        type="radio" 
                        name="question2" 
                        onChange={() => {
                          setQuestion2Step(2);
                        }}
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                    
                    <label className="flex p-4 cursor-pointer bg-white border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
                      <span className="text-gray-300 font-semibold">{t("progress.answers.no")}</span>
                      <input 
                        type="radio" 
                        name="question2" 
                        disabled 
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>

                    <label className="flex p-4 cursor-pointer bg-white border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
                      <span className="text-gray-300 font-semibold">{t("progress.answers.partially")}</span>
                      <input 
                        type="radio" 
                        name="question2" 
                        disabled
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                  
                  <div>
                    <h3 className="block mb-2 text-sm font-medium text-gray-700">
                      {t("progress.file.supportingDocs")} (<span className="text-red-500 font-semibold">{t("progress.file.supportedTypes")}</span>)
                    </h3>
                    <div className="mt-1 relative">
                      <div className={`bg-white border ${
                        activeQuestion === 2 && question2Step === 2 && !filesUploaded 
                          ? "border-blue-500 shadow-md animate-pulse" 
                          : "border-gray-300"
                      } rounded-md px-3 py-2`}>
                        {activeQuestion === 2 && question2Step === 2 &&  !filesUploaded && (
                          <div className="absolute -right-2 -top-2">
                            <Lottie
                              animationData={animationData}
                              loop={true}
                              style={{ width: 60, height: 60 }}
                            />
                          </div>
                        )}
                        <input
                          type="file"
                          onChange={handleFileChange}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="block mb-2 text-sm font-medium text-gray-700">{t("progress.comments.label")}</h3>
                    <textarea
                      disabled
                      rows={3}
                      className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm placeholder-gray-400 opacity-50 focus:border-blue-500 focus:ring-blue-500"
                      placeholder={t("progress.comments.placeholder")}
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={question2Done || !validButton2}
                      onClick={handleQuestion2Submit}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 relative disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {t("progress.buttons.validate")}
                      {validButton2 && (
                        <div className="absolute -right-1 -top-1">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 70, height: 70 }}
                          />
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>


            {/* Question 3 */}
            <div 
              ref={question3Ref}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">{t("progress.questions.question")} 3 {t("progress.questions.outOf")} 5</h2>
                </div>
                
                <div className="space-y-6">
                  <p className="text-gray-700 font-medium">
                    {t("demo.question3")}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <label className="flex p-4 cursor-pointer bg-white border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
                      <span className="text-gray-300 font-semibold">{t("progress.answers.yes")}</span>
                      <input 
                        type="radio" 
                        name="question3" 
                        disabled
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                    
                    <label className="flex p-4 cursor-pointer bg-white border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
                      <span className="text-gray-300 font-semibold">{t("progress.answers.no")}</span>
                      <input 
                        type="radio" 
                        name="question3" 
                        disabled 
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>

                    <label 
                      className={`flex p-4 cursor-pointer bg-white border ${
                        activeQuestion === 3 && question3Step === 1 
                          ? "border-blue-500 shadow-md animate-pulse" 
                          : "border-gray-200"
                      } rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all relative`}
                    >
                      {activeQuestion === 3 && question3Step === 1 && (
                        <div className="absolute -right-2 -top-2">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 60, height: 60 }}
                          />
                        </div>
                      )}
                      <span className="text-gray-800 font-semibold">{t("progress.answers.partially")}</span>
                      <input 
                        type="radio" 
                        name="question3" 
                        value="partiellement"
                        onChange={() => {
                          setValidButton3(true)
                          setQuestion3Step(2)
                        }}
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                  
                  <div>
                    <h3 className="block mb-2 text-sm font-medium text-gray-400">
                      {t("progress.file.supportingDocs")} (<span className="text-red-300 font-semibold">{t("progress.file.supportedTypes")}</span>)
                    </h3>
                    <div className="mt-1">
                      <div className="bg-gray-50 border border-gray-300 rounded-md px-3 py-2 opacity-50">
                        <input
                          type="file"
                          disabled
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-200 file:text-gray-700"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="block mb-2 text-sm font-medium text-gray-700">{t("progress.comments.label")}</h3>
                    <div className="relative">
                      <textarea
                        rows={3}
                        value={textareaValue}
                        onChange={handleTextareaChangeQ3}
                        className={`block w-full rounded-md border ${
                          activeQuestion === 3 && question3Step === 2
                            ? "border-blue-500 shadow-sm animate-pulse" 
                            : "border-gray-300"
                        } py-2 px-3 text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500`}
                        placeholder={t("progress.comments.addHere")}
                      ></textarea>
                      {activeQuestion === 3 && question3Step === 2 && (
                        <div className="absolute -right-2 -top-2">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 60, height: 60 }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={question3Done || !validButton3}
                      onClick={handleQuestion3Submit}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 relative disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {t("progress.buttons.validate")}
                      {validButton3 && question3Step === 3 && (
                        <div className="absolute -right-1 -top-1">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 70, height: 70 }}
                          />
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Question 4 */}
            <div 
              ref={question4Ref}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">{t("progress.questions.question")} 4 {t("progress.questions.outOf")} 5</h2>
                </div>
                
                <div className="space-y-6">
                  <p className="text-gray-700 font-medium">
                    {t("demo.question4")}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <label 
                      className={`flex p-4 cursor-pointer bg-white border ${
                        activeQuestion === 4 && question4Step === 1
                          ? "border-blue-500 shadow-md animate-pulse" 
                          : "border-gray-200"
                      } rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all relative`}
                    >
                      {activeQuestion === 4 && question4Step === 1 && (
                        <div className="absolute -right-2 -top-2">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 60, height: 60 }}
                          />
                        </div>
                      )}
                      <span className="text-gray-800 font-semibold">{t("progress.answers.yes")}</span>
                      <input 
                        type="radio" 
                        name="question4" 
                        onChange={() => {
                          setValidButton4(true);
                          setQuestion4Step(2)
                        }}
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                    
                    <label className="flex p-4 cursor-pointer bg-white border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
                      <span className="text-gray-300 font-semibold">{t("progress.answers.no")}</span>
                      <input 
                        type="radio" 
                        name="question4" 
                        disabled 
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>

                    <label className="flex p-4 cursor-pointer bg-white border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
                      <span className="text-gray-800 font-semibold">{t("progress.answers.partially")}</span>
                      <input 
                        type="radio" 
                        name="question4" 
                        value="partiellement"
                        onChange={() => setValidButton4(true)}
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                  
                  <div>
                    <h3 className="block mb-2 text-sm font-medium text-gray-700">
                      {t("progress.file.supportingDocs")} (<span className="text-red-500 font-semibold">{t("progress.file.supportedTypes")}</span>)
                    </h3>
                    <div className="mt-1 relative">
                      <div className={`bg-white border ${
                        activeQuestion === 4 && question4Step === 3 && question4Done === false && file4Uploaded === false
                          ? "border-blue-500 shadow-md animate-pulse" 
                          : "border-gray-300"
                      } rounded-md px-3 py-2`}>
                        {activeQuestion === 4 && question4Step === 3 && question4Done === false && file4Uploaded === false && (
                          <div className="absolute -right-2 -top-2">
                            <Lottie
                              animationData={animationData}
                              loop={true}
                              style={{ width: 60, height: 60 }}
                            />
                          </div>
                        )}
                        <input
                          type="file"
                          onChange={handleFileChangeQuestion4}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="block mb-2 text-sm font-medium text-gray-700">{t("progress.comments.label")}</h3>
                    <textarea
                      disabled
                      rows={3}
                      className="block w-full rounded-md border border-gray-300 py-2 px-3 text-sm placeholder-gray-400 opacity-50 focus:border-blue-500 focus:ring-blue-500"
                      placeholder={t("progress.comments.placeholder")}
                    ></textarea>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={question4Done || !validButton4}
                      onClick={handleQuestion4Submit}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 relative disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {t("progress.buttons.validate")}
                      {validButton4 && (question4Step === 2 || question4Step === 4) && (
                        <div className="absolute -right-1 -top-1">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 70, height: 70 }}
                          />
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Question 5 */}
            <div 
              ref={question5Ref}
              className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-800">{t("progress.questions.question")} 5 {t("progress.questions.outOf")} 5</h2>
                </div>
                
                <div className="space-y-6">
                  <p className="text-gray-700 font-medium">
                    {t("demo.question5")}
                  </p>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <label className="flex p-4 cursor-pointer bg-white border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
                      <span className="text-gray-300 font-semibold">{t("progress.answers.yes")}</span>
                      <input 
                        type="radio" 
                        name="question5" 
                        disabled 
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                    
                    <label className="flex p-4 cursor-pointer bg-white border border-gray-200 rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all">
                      <span className="text-gray-300 font-semibold">{t("progress.answers.no")}</span>
                      <input 
                        type="radio" 
                        name="question5" 
                        disabled
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>

                    <label 
                      className={`flex p-4 cursor-pointer bg-white border ${
                        activeQuestion === 5 && question5Step === 1
                          ? "border-blue-500 shadow-md animate-pulse" 
                          : "border-gray-200"
                      } rounded-lg focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200 transition-all relative`}
                    >
                      {activeQuestion === 5 && question5Step === 1 && (
                        <div className="absolute -right-2 -top-2">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 60, height: 60 }}
                          />
                        </div>
                      )}
                      <span className="text-gray-800 font-semibold">{t("progress.answers.partially")}</span>
                      <input 
                        type="radio" 
                        name="question5" 
                        value="partiellement"
                        onChange={() => { 
                          setValidButton5(true);
                          setQuestion5Step(2);
                        }}
                        className="ml-auto mt-0.5 border-gray-200 rounded-full text-blue-600 focus:ring-blue-500"
                      />
                    </label>
                  </div>
                  
                  <div>
                    <h3 className="block mb-2 text-sm font-medium text-gray-700">
                      {t("progress.file.supportingDocs")} (<span className="text-red-500 font-semibold">{t("progress.file.supportedTypes")}</span>)
                    </h3>
                    <div className="mt-1">
                      <div className="bg-gray-50 border border-gray-300 rounded-md px-3 py-2 opacity-50">
                        <input
                          type="file"
                          disabled
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-200 file:text-gray-700"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="block mb-2 text-sm font-medium text-gray-700">{t("progress.comments.label")}</h3>
                    <div className={`relative ${commentRequired ? "animate-pulse" : ""}`}>
                      <textarea
                        rows={3}
                        className={`block w-full rounded-md border ${
                          commentRequired ? "border-blue-500" : "border-gray-300"
                        } py-2 px-3 text-sm placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500`}
                        placeholder={t("progress.comments.required")}
                        onChange={handleTextareaChangeQ5}
                      ></textarea>
                      {commentRequired && question5Step === 3 && (
                        <div className="absolute -right-2 -top-2">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 60, height: 60 }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-end">
                    <button
                      type="button"
                      disabled={question5Done || !validButton5}
                      onClick={handleQuestion5Submit}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 relative disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                      {t("progress.buttons.validate")}
                      {validButton5 && (question5Step === 2 || question5Step === 4) && (
                        <div className="absolute -right-1 -top-1">
                          <Lottie
                            animationData={animationData}
                            loop={true}
                            style={{ width: 70, height: 70 }}
                          />
                        </div>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Progress;